import { Search_RequestBody } from '@opensearch-project/opensearch/api';
import { QueryContainer } from '@opensearch-project/opensearch/api/_types/_common.query_dsl';
import cloneDeep from 'lodash/cloneDeep';

import { EnumCustomerType } from '@/types';
import {
  InitialProductQueryFilters,
  PRODUCT_FILTER_ATTRIBUTES,
} from '@/constants';

import {
  buildFacetQueries,
  buildQueryFilterFromSearchParams,
  buildSearchQueryFilter,
  buildSmartFacetAggregations,
  buildSortFilterFromSearchParams,
  getPriceFilter,
} from './facets';

const getCustomerTypes = (
  customerType?: EnumCustomerType,
  isStrictMedicalEnabled?: boolean,
): EnumCustomerType[] => {
  if (customerType === EnumCustomerType.MEDICAL) {
    return isStrictMedicalEnabled
      ? [EnumCustomerType.MEDICAL]
      : [
          EnumCustomerType.MEDICAL,
          EnumCustomerType.ADULT,
          EnumCustomerType.BOTH,
        ];
  }
  return [EnumCustomerType.ADULT, EnumCustomerType.BOTH];
};

const injectCustomerTypeToSearchBody = ({
  searchBody,
  customerType,
  isStrictMedicalEnabled,
}: {
  searchBody: Search_RequestBody;
  customerType?: EnumCustomerType;
  isStrictMedicalEnabled?: boolean;
}) => {
  const body = cloneDeep(searchBody);

  const customerTypes = getCustomerTypes(customerType, isStrictMedicalEnabled);

  if (customerType && customerType !== EnumCustomerType.ALL) {
    const customerTypeQueryContainer = {
      bool: {
        should: [
          ...customerTypes.map(type => ({
            match: {
              customCustomerType: type,
            },
          })),
        ],
      },
    } as QueryContainer;

    if (body.aggs?.facets?.aggs) {
      body.aggs.facets.aggs = Object.entries(
        body.aggs?.facets?.aggs || {},
      ).reduce((acc, [key, value]) => {
        const hasFilter = !!(value as any).filter;

        acc[key] = hasFilter
          ? {
              ...value,
              filter: {
                bool: {
                  must: [
                    ...(value as any).filter.bool.must,
                    customerTypeQueryContainer,
                  ],
                },
              },
            }
          : value;

        return acc;
      }, {} as Record<string, any>) as Record<string, any>;
    }

    (body.query?.bool?.must as QueryContainer[])?.push(
      customerTypeQueryContainer,
    );
  }

  return body;
};

const buildPriceFilterFromQueryRecord = (
  queryFiltersByFacets: Record<string, QueryContainer>,
  includePromoItems: boolean,
): Record<string, QueryContainer> => {
  const priceFilterStartValue = (
    queryFiltersByFacets[PRODUCT_FILTER_ATTRIBUTES._PRICE]?.bool
      ?.filter as QueryContainer
  )?.range?.[PRODUCT_FILTER_ATTRIBUTES._PRICE]?.gte;
  const priceFilterEndValue = (
    queryFiltersByFacets[PRODUCT_FILTER_ATTRIBUTES._PRICE]?.bool
      ?.filter as QueryContainer
  )?.range?.[PRODUCT_FILTER_ATTRIBUTES._PRICE]?.lte;

  const priceQuery = getPriceFilter({
    includePromoItems,
    priceStart: priceFilterStartValue
      ? Number(priceFilterStartValue)
      : undefined,
    priceEnd: priceFilterEndValue ? Number(priceFilterEndValue) : undefined,
  });
  return {
    [PRODUCT_FILTER_ATTRIBUTES._PRICE]: priceQuery,
  };
};

const buildSearchBodyFromParams = ({
  searchParams = {},
  filters = {},
  limit = 20,
  includePromoItems = false,
}: {
  searchParams?: Record<string, string | string[] | boolean[]>;
  filters?: Record<string, string | string[] | boolean[]>;
  limit?: number;
  includePromoItems?: boolean;
}) => {
  const sortFilter = buildSortFilterFromSearchParams(searchParams);
  const searchFilter = buildSearchQueryFilter(
    (searchParams.query || '') as string,
  );
  const page = parseInt((searchParams.page || '1') as string);
  const from = (page - 1) * limit;

  const queryFilters = buildQueryFilterFromSearchParams({
    ...InitialProductQueryFilters,
    ...searchParams,
    ...filters,
  });

  const priceFilter = buildPriceFilterFromQueryRecord(
    queryFilters,
    includePromoItems,
  );

  const searchBody: Search_RequestBody = {
    from,
    size: limit,
    query: buildFacetQueries({
      ...queryFilters,
      ...searchFilter,
      ...priceFilter,
    }),
    aggs: buildSmartFacetAggregations({
      ...queryFilters,
      ...searchFilter,
      ...priceFilter,
    }),
    ...(sortFilter ? { sort: sortFilter } : {}),
  };

  return { searchBody };
};

export {
  injectCustomerTypeToSearchBody,
  buildSearchBodyFromParams,
  buildPriceFilterFromQueryRecord,
};
